import { checkOnboarding } from '@/core/services/OnboardingChecker';
import type { User } from '@/types/User';
import { defineStore } from 'pinia';

export const useAuthStore = defineStore('auth', () => {
  const { data } = useAuth();
  const user = ref<User>(data.value?.data || null);

  const getUser = computed(() => user.value || ({} as User));
  const getUserFullname = computed(() => {
    return hasLoggedInUser.value
      ? getUser.value.full_name || getUser.value.first_name + ' ' + getUser.value.last_name
      : '';
  });
  const getCompanyName = computed(() => {
    return hasLoggedInUser.value ? getUser.value?.company?.name : '';
  });
  const getRole = computed(() => {
    return hasLoggedInUser.value ? getUser.value?.employee?.role : '';
  });
  const isAccountOwner = computed((): boolean => {
    return hasLoggedInUser.value ? getUser.value?.employee?.is_account_owner : false;
  });
  const isManager = computed((): boolean => {
    return hasLoggedInUser.value ? getUser.value?.employee?.is_manager : false;
  });
  const isSeniorTeamLeader = computed((): boolean => {
    return hasLoggedInUser.value ? getUser.value?.employee?.is_senior_team_leader : false;
  });
  const isRegularEmployee = computed((): boolean => {
    return hasLoggedInUser.value ? getUser.value?.employee?.is_regular_employee : false;
  });

  const hasLoggedInUser = computed(() => Boolean(user.value));
  const hasCompletedOnboarding = computed(() => user.value?.first_name && user.value?.last_name);

  const isVerified = computed(() => user.value?.email_verified);
  const onboardingCompleted = computed(() => checkOnboarding(getUser.value));

  watch(
    () => data.value,
    () => {
      if (data.value?.data) {
        user.value = data.value?.data;
      }
    },
    { immediate: true }
  );

  return {
    user,

    getUser,
    getUserFullname,
    getCompanyName,
    getRole,

    isAccountOwner,
    isManager,
    isRegularEmployee,
    isSeniorTeamLeader,

    hasLoggedInUser,
    hasCompletedOnboarding,

    isVerified,
    onboardingCompleted,
  };
});
